// Presentation only: move the existing controls without creating another serializer.
// Collapsed fields remain mounted, including custom values the editor does not own.
import { connectEditorLabels, describeEditorControl } from './magnet-editor-accessibility.js';
import { flowStepTitle as stepTitle, flowOverviewSummary } from './magnet-flow-outline.js';
import { answerStorageSummary, mountAnswerSettings, updateAnswerSettings } from './magnet-answer-settings.js';
import '../styles/magnet-flow-focus.css';

const isChoice = step => ['single_select', 'multi_select'].includes(step.step_type);
const destinationName = (step, option, model) => {
  const target = option?.goto ? model.find(item => String(item.id) === String(option.goto)) : model[model.indexOf(step) + 1];
  return target ? `“${stepTitle(target)}”` : option?.goto ? 'Missing destination' : 'Finish & send answers';
};
const make = (tag, className, text) => {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text) element.textContent = text;
  return element;
};
export function rememberFlowDisclosures(canvas, state) {
  for (const node of canvas.querySelectorAll('.flow-node')) {
    const groups = node.querySelectorAll('details[data-flow-detail]');
    const body = node.querySelector('.flow-node__body');
    if (body) state.set(node.dataset.uid, { ...Object.fromEntries([...groups].map(group => [group.dataset.flowDetail, group.open])), task: body.dataset.flowTask || 'wording' });
    else if (state.has(node.dataset.uid)) state.set(node.dataset.uid, { ...state.get(node.dataset.uid), task: 'wording' });
  }
}

function details(body, key, title, saved) {
  const group = make('details', 'flow-detail');
  group.dataset.flowDetail = key;
  group.append(make('summary', '', title));
  group.open = !!saved?.[key];
  body.append(group);
  return group;
}

// "Shown as" is an override most people never need: it lives inside the step's
// existing group (Answer settings for a question, Ending behavior for a message)
// rather than on every card. The control stays mounted either way.
function movePresentation(body, group) {
  const select = body.querySelector('.flow-f-presentation');
  if (!select) return;
  const label = select.closest('label');
  const hint = label?.nextElementSibling;
  if (label) group.append(label);
  if (hint?.classList.contains('mag-modal__hint')) group.append(hint);
}

function moveControl(group, control, hint = false) {
  if (!control) return;
  const label = control.previousElementSibling;
  const help = control.nextElementSibling;
  if (label?.tagName === 'LABEL') group.append(label);
  group.append(control);
  if (hint && help?.classList.contains('mag-modal__hint')) group.append(help);
}

export function updateFlowDisclosure(node, step, model, options = {}) {
  if (!node || !step) return;
  const title = node.querySelector('.flow-node__title');
  if (title) title.textContent = stepTitle(step);
  node.querySelector('.flow-node__toggle')?.setAttribute('aria-label', `${node.querySelector('.flow-node__body') ? 'Finish editing' : 'Edit'} ${step.step_type === 'message' ? 'message' : 'question'} ${model.indexOf(step) + 1}: ${stepTitle(step)}`);
  const summary = node.querySelector('[data-flow-summary]');
  if (summary) {
    const facts = [];
    if (step.step_type !== 'message') {
      facts.push(step.step_columns.length ? answerStorageSummary(step)
        : ['single_select', 'multi_select'].includes(step.step_type) ? 'Navigation only · no answer saved' : 'Choose where to save this answer');
      if (step.step_config?.optional) facts.push('Optional');
    }
    if (step.step_config?.allow_overwrite) facts.push('Replaces an earlier answer');
    if (step.step_config?.skip_if_prefilled) facts.push('Skipped when answer is known');
    if (step.step_config?.terminal) facts.push('Ends here without sending answers');
    if (step.step_config?.presentation === 'bubbles') facts.push('Shown as messages');
    if (step.step_config?.presentation === 'heading') facts.push('Shown as a question heading');
    const branches = step.step_options.filter(option => option.goto);
    if (branches.length) facts.push(`${branches.length} branching ${branches.length === 1 ? 'choice' : 'choices'}`);
    if (branches.some(option => !model.some(target => String(target.id) === String(option.goto)))) facts.push('Missing destination');
    if (step.step_options.some(option => option.config && Object.keys(option.config).length)) facts.push('Custom choice behavior');
    if (!step.step_config?.terminal && !branches.length) facts.push(model.indexOf(step) < model.length - 1 ? `Next: question ${model.indexOf(step) + 2}` : 'Then: Finish & send answers');
    summary.textContent = node.querySelector('.flow-node__body') ? facts.join(' · ') : flowOverviewSummary(step, model);
  }
  const mapping = node.querySelector('[data-flow-mapping-needed]');
  if (mapping) mapping.hidden = step.step_columns.length > 0 || isChoice(step);
  const optional = node.querySelector('.flow-f-optional');
  const skipLabel = node.querySelector('.flow-f-skip-label')?.closest('label');
  if (skipLabel) skipLabel.hidden = !optional?.checked;
  const answerTitle = node.querySelector('[data-flow-detail="answers"] > summary');
  if (answerTitle) answerTitle.textContent = step.step_config?.allow_overwrite || step.step_config?.skip_if_prefilled
    ? 'More answer behavior · customized' : 'More answer behavior';
  updateAnswerSettings(node, step, options);
  node.querySelectorAll('.flow-opt').forEach((row, index) => {
    const option = step.step_options[index];
    const title = row.querySelector('summary');
    if (!title || !option) return;
    const parts = ['Appearance'];
    if (option.icon) parts.push('Icon');
    if (option.description) parts.push('Description');
    if (option.config && Object.keys(option.config).length) parts.push('Custom behavior');
    title.textContent = parts.join(' · ');
    const destination = row.querySelector('.flow-o-goto');
    const next = row.querySelector('[data-flow-destination]');
    if (destination) {
      destination.options[0].textContent = `Continue to ${destinationName(step, null, model)}`;
      for (const item of destination.options) {
        if (!item.value) continue;
        const target = model.find(candidate => String(candidate.id) === item.value);
        if (target && target !== step) item.textContent = stepTitle(target);
      }
    }
    if (next) {
      next.textContent = `After this choice: ${destinationName(step, option, model)}`;
      next.classList.toggle('flow-choice-destination--missing', !!option.goto && !model.some(item => String(item.id) === String(option.goto)));
    }
  });
}

function showTask(body, name, focus = false) {
  const panel = body.querySelector(`[data-flow-task-panel="${name}"]`);
  if (!panel) return;
  body.dataset.flowTask = name;
  if (name === 'answers') body.dispatchEvent(new CustomEvent('flow-answer-settings-open'));
  for (const item of body.querySelectorAll('[data-flow-task-panel]')) item.hidden = item !== panel;
  for (const button of body.querySelectorAll('[data-flow-task-tab]')) {
    const active = button.dataset.flowTaskTab === name;
    button.setAttribute('aria-selected', String(active)); button.tabIndex = active ? 0 : -1;
    if (active && focus) button.focus({ preventScroll: true });
  }
}

function mountTasks(body, step, saved, options) {
  body.addEventListener('flow-answer-settings-open', () => options.onRequestDelivery?.());
  const definitions = [
    ['wording', 'Wording', step.step_type === 'message' ? 'Write what visitors should see at this point.' : 'Ask one clear question in your own words.'],
    ...(isChoice(step) ? [['choices', step.step_type === 'single_select' ? 'Choices & paths' : 'Choices', step.step_type === 'single_select' ? 'Add the answers visitors can choose and where each one leads.' : 'Add the choices visitors can select together.']] : []),
    ...(step.step_type === 'scheduler' ? [['schedule', 'Availability', 'Set the times visitors can request for your team to confirm.']] : []),
    ...(step.step_type === 'message' ? [['ending', 'Ending', 'Choose whether this message continues the conversation or ends it.']] : [['answers', 'Answer settings', 'See where this answer goes when a visitor submits.']]),
  ];
  const panels = Object.fromEntries(definitions.map(([key, label, help]) => {
    const panel = make('section', 'flow-task-panel');
    panel.dataset.flowTaskPanel = key; panel.id = `${body.id}-${key}`;
    panel.setAttribute('role', 'tabpanel'); panel.setAttribute('aria-labelledby', `${panel.id}-tab`);
    panel.append(make('p', 'flow-task-help', help));
    return [key, panel];
  }));
  const prompts = body.querySelector('.flow-f-prompts');
  prompts.previousElementSibling.textContent = step.step_type === 'message' ? 'Message' : 'Your question';
  moveControl(panels.wording, prompts);
  if (step.step_prompts.length > 1) prompts.after(make('p', 'mag-modal__hint', step.step_type === 'message' ? 'Each line appears as a separate message.' : 'Each line appears separately. The last line asks the question.'));
  const type = body.querySelector('.flow-f-type');
  type.previousElementSibling.textContent = step.step_type === 'message' ? 'Content type' : 'Visitors answer with';
  moveControl(panels.wording, type);
  const grouping = body.querySelector('.flow-question-group');
  if (grouping) details(panels.wording, 'grouping', step.step_config?.group_with_previous ? 'Screen layout · grouped' : 'Screen layout', saved).append(grouping);
  const placeholder = body.querySelector('.flow-f-placeholder');
  if (placeholder) details(panels.wording, 'wording', step.step_config?.placeholder ? 'Input hint · customized' : 'Input hint', saved).append(placeholder.closest('label'));

  const field = body.querySelector('.flow-f-field');
  if (field) {
    for (const selector of ['.flow-f-optional', '.flow-f-skip-label']) {
      const control = body.querySelector(selector); if (control) panels.answers.append(control.closest('label'));
    }
    const advanced = details(panels.answers, 'answers', 'More answer behavior', saved);
    for (const selector of ['.flow-f-overwrite', '.flow-f-prefilled']) {
      const control = body.querySelector(selector); if (control) advanced.append(control.closest('label'));
    }
    movePresentation(body, advanced);
    mountAnswerSettings(panels.answers, field, step, saved, advanced, options);
  }

  if (panels.choices) {
    moveControl(panels.choices, body.querySelector('.flow-opts'));
    panels.choices.append(body.querySelector('[data-x="addopt"]'));
    panels.choices.querySelectorAll('.flow-opt').forEach((row, index) => {
      row.querySelector('.flow-o-label').setAttribute('aria-label', `Choice ${index + 1}`);
      row.querySelector('[data-x="delopt"]').setAttribute('aria-label', `Remove choice ${index + 1}`);
      const destination = row.querySelector('.flow-o-goto');
      if (destination) {
        destination.setAttribute('aria-label', `Choice ${index + 1} destination`);
        destination.closest('label').classList.add('flow-choice-path');
        const summary = make('p', 'flow-choice-destination'); summary.dataset.flowDestination = '';
        destination.closest('label').after(summary);
      }
      const group = details(row, `choice-${index}`, 'Appearance', saved);
      for (const [selector, label] of [['.flow-o-icon', 'Icon'], ['.flow-o-description', 'Description']]) {
        const input = row.querySelector(selector); input.setAttribute('aria-label', `Choice ${index + 1} ${label.toLowerCase()}`);
        const wrapper = make('label', 'mag-modal__label', label); wrapper.append(input); group.append(wrapper);
      }
      if (step.step_options[index]?.config && Object.keys(step.step_options[index].config).length) {
        const note = make('p', 'flow-choice-custom', 'Custom behavior is configured for this choice and will be preserved.');
        row.insertBefore(note, group);
      }
    });
  }

  if (panels.ending) {
    const terminal = body.querySelector('.flow-f-terminal');
    const row = terminal.closest('.mag-tog-row');
    row.querySelector('label').textContent = 'End here without sending answers';
    const hint = row.nextElementSibling;
    panels.ending.append(row);
    if (hint?.classList.contains('mag-modal__hint')) panels.ending.append(hint);
    moveControl(panels.ending, body.querySelector('.flow-f-cta-label'));
    moveControl(panels.ending, body.querySelector('.flow-f-cta-url'));
    const warning = body.querySelector('.flow-cta-warn');
    if (warning) {
      const help = warning.nextElementSibling; panels.ending.append(warning);
      if (help?.classList.contains('mag-modal__hint')) panels.ending.append(help);
    }
    movePresentation(body, details(panels.ending, 'ending', 'Message appearance', saved));
  }
  if (panels.schedule) {
    moveControl(panels.schedule, body.querySelector('.flow-f-mtlabel'));
    const row = body.querySelector('.flow-f-start').closest('.mag-modal__row');
    const hint = row.nextElementSibling; panels.schedule.append(row);
    if (hint?.classList.contains('mag-modal__hint')) panels.schedule.append(hint);
  }
  // Unknown future controls stay available, mounted, and readable by the host.
  panels.wording.append(...body.childNodes);
  const nav = make('div', 'flow-task-tabs'); nav.setAttribute('role', 'tablist'); nav.setAttribute('aria-label', 'Edit this conversation item');
  for (const [key, label] of definitions) {
    const button = make('button', 'flow-task-tab', label); button.type = 'button'; button.dataset.flowTaskTab = key;
    button.id = `${panels[key].id}-tab`; button.setAttribute('role', 'tab'); button.setAttribute('aria-controls', panels[key].id);
    nav.append(button);
  }
  body.append(nav);
  if (field && !isChoice(step)) {
    const needed = make('button', 'flow-mapping-needed', 'Choose where to save this answer →');
    needed.type = 'button'; needed.dataset.flowMappingNeeded = '';
    needed.addEventListener('click', () => {
      showTask(body, 'answers');
      field.closest('details').open = true;
      field.focus({ preventScroll: true });
    });
    body.append(needed);
  }
  body.append(...Object.values(panels));
  nav.addEventListener('click', event => {
    const tab = event.target.closest('[data-flow-task-tab]'); if (tab) showTask(body, tab.dataset.flowTaskTab);
  });
  nav.addEventListener('keydown', event => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const buttons = [...nav.children], index = buttons.indexOf(event.target);
    if (index < 0) return;
    event.preventDefault(); event.stopPropagation();
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
    showTask(body, buttons[next].dataset.flowTaskTab, true);
  });
  definitions.slice(0, -1).forEach(([key], index) => {
    const [next, label] = definitions[index + 1];
    const button = make('button', 'flow-task-next', `Next: ${label.toLowerCase()} →`);
    button.type = 'button'; button.dataset.flowTaskNext = next;
    button.addEventListener('click', () => {
      showTask(body, next, true);
      nav.scrollIntoView?.({ block: 'nearest' });
    });
    panels[key].append(button);
  });
  showTask(body, panels[saved?.task] ? saved.task : 'wording');
}

export function enhanceFlowDisclosures(canvas, model, state, options = {}) {
  canvas.classList.add('flow-canvas--progressive');
  for (const [index, step] of model.entries()) {
    const node = canvas.querySelector(`[data-uid="${step._uid}"]`);
    const body = node.querySelector('.flow-node__body');
    const toggle = make('button', 'flow-node__toggle');
    toggle.type = 'button';
    toggle.setAttribute('aria-expanded', String(!!body));
    toggle.setAttribute('aria-label', `${body ? 'Finish editing' : 'Edit'} ${step.step_type === 'message' ? 'message' : 'question'} ${index + 1}: ${stepTitle(step)}`);
    const heading = make('span', 'flow-node__heading');
    heading.append(make('span', 'flow-node__title', stepTitle(step)));
    const metadata = make('span', 'flow-node__caption');
    metadata.append(node.querySelector('.flow-node__type'), node.querySelector('.flow-node__n'));
    heading.append(metadata);
    toggle.append(heading);
    node.querySelector('.flow-node__hd').insertBefore(toggle, node.querySelector('.flow-node__tools'));
    for (const [action, label] of [['up', `Move question ${index + 1} up`], ['down', `Move question ${index + 1} down`], ['del', `Delete question ${index + 1}`]]) {
      node.querySelector(`[data-x="${action}"]`).setAttribute('aria-label', label);
    }
    const summary = make('p', 'flow-behavior-summary'); summary.dataset.flowSummary = '';
    node.querySelector('.flow-node__hd').after(summary);
    if (body) {
      body.id = `flow-editor-${step._uid}`;
      toggle.setAttribute('aria-controls', body.id);
      mountTasks(body, step, state.get(step._uid), options);
      // Associate the existing standalone labels without changing stored values.
      connectEditorLabels(body);
      const done = make('button', 'mag-modal__btn mag-modal__btn--ghost flow-done', 'Back to questions');
      done.type = 'button'; done.dataset.x = 'done'; body.append(done);
    }
    updateFlowDisclosure(node, step, model, options);
    if (!body) enhanceOverviewCard(node, step, index);
  }
}

function enhanceOverviewCard(node, step, index) {
  const kind = step.step_type === 'message' ? 'message' : 'question';
  const caption = node.querySelector('.flow-node__caption');
  const number = node.querySelector('.flow-node__n');
  number.textContent = `${kind === 'message' ? 'Message' : 'Question'} ${index + 1}`;
  caption.prepend(number);
  if (isChoice(step)) node.querySelector('.flow-node__type').textContent = step.step_type === 'multi_select' ? 'Select any that apply' : 'Choose one answer';
  const heading = node.querySelector('.flow-node__heading');
  heading.prepend(caption);
  heading.append(make('span', 'flow-node__edit', `Edit ${kind} →`));
  // Answer labels are a summary, not visitor controls. The real choices remain
  // available on the canvas and in the focused Choices & paths task.
  const preview = node.querySelector('.flow-node__preview');
  preview.replaceChildren();
  const labels = step.step_options.map(option => String(option.label || '').trim()).filter(Boolean);
  if (isChoice(step) && labels.length) {
    preview.classList.add('flow-choice-preview');
    preview.append(make('span', 'flow-choice-preview__label', `${labels.length} ${labels.length === 1 ? 'choice' : 'choices'}: `));
    preview.append(make('span', '', labels.slice(0, 2).join(' · ')));
    if (labels.length > 2) preview.append(make('span', 'flow-choice-preview__more', ` · ${labels.length - 2} more ${labels.length - 2 === 1 ? 'choice' : 'choices'}`));
  }
  node.append(node.querySelector('[data-flow-summary]'));
  const tools = node.querySelector('.flow-node__tools');
  const actions = make('details', 'flow-card-actions');
  actions.name = 'flow-card-actions';
  const summary = make('summary', '', 'Actions');
  summary.setAttribute('aria-label', `Actions for ${kind} ${index + 1}`);
  actions.append(summary, tools);
  for (const [action, label] of [['up', 'Move earlier'], ['down', 'Move later'], ['del', `Delete ${kind}`]]) {
    const button = tools.querySelector(`[data-x="${action}"]`);
    button.append(make('span', '', label));
    button.setAttribute('aria-label', action === 'del' ? `${label} ${index + 1}` : `${label}: ${kind} ${index + 1}`);
  }
  actions.addEventListener('keydown', event => {
    if (event.key === 'Escape' && actions.open) {
      event.preventDefault(); event.stopPropagation(); actions.open = false; summary.focus();
    }
  });
  node.querySelector('.flow-node__hd').append(actions);
}

export function revealFlowControl(control) {
  if (!control) return;
  const body = control.closest('.flow-node__body');
  const panel = control.closest('[data-flow-task-panel]');
  if (body && panel) showTask(body, panel.dataset.flowTaskPanel);
  for (let parent = control.parentElement; parent; parent = parent.parentElement) {
    if (parent.tagName === 'DETAILS') parent.open = true;
  }
  control.setAttribute('aria-invalid', 'true');
  const error = document.getElementById('flow-error');
  if (error?.textContent) describeEditorControl(control, error);
  control.focus?.();
}
